import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ScanLine, Check, Truck, MapPin, Package, AlertTriangle, RotateCcw } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';
import { CourierShell, C } from '../../components/courier/CourierUI';

const hasToken = () => !!localStorage.getItem('tekstar_token');
const ACTIONS = [
  { s: 'received', label: 'Received', icon: Package },
  { s: 'shipped', label: 'Shipped', icon: Truck },
  { s: 'out_for_delivery', label: 'Out for Delivery', icon: MapPin },
  { s: 'exception', label: 'Exception', icon: AlertTriangle },
];

export default function CourierOps() {
  const [authed, setAuthed] = useState(null);
  const [name, setName] = useState('');
  const [awb, setAwb] = useState('');
  const [shipment, setShipment] = useState(null);
  const [pod, setPod] = useState({ receivedBy: '', note: '' });
  const [reason, setReason] = useState('');
  const [queue, setQueue] = useState([]);

  useEffect(() => {
    if (!hasToken()) { setAuthed(false); return; }
    api.get('/courier/ops/me').then(({ data }) => { setAuthed(true); setName(data.name); loadQueue(); })
      .catch(() => setAuthed(false));
  }, []);

  const loadQueue = () => api.get('/courier/ops/shipments?status=out_for_delivery').then(({ data }) => setQueue(data.shipments)).catch(() => {});

  const lookup = async (code) => {
    const c = (code || awb).trim().toUpperCase();
    if (!c) return;
    try {
      const { data } = await api.get(`/courier/track/${c}`);
      setShipment(data); setAwb(c);
    } catch (e) {
      toast({ title: 'Not found', description: 'No shipment for that AWB.', variant: 'destructive' });
      setShipment(null);
    }
  };

  const scan = async (status) => {
    try {
      await api.post(`/courier/ops/shipments/${awb}/scan`, { status, hub: 'Hub', reason: status === 'exception' ? (reason || 'Delivery exception') : undefined });
      toast({ title: 'Updated', description: `Marked ${status.replace(/_/g, ' ')}.` });
      setReason(''); lookup(awb); loadQueue();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Scan failed.', variant: 'destructive' });
    }
  };

  const reattempt = async () => {
    try {
      await api.post(`/courier/ops/shipments/${awb}/reattempt`, { note: reason || 'Re-attempt scheduled' });
      toast({ title: 'Re-attempt scheduled', description: 'Back out for delivery.' });
      setReason(''); lookup(awb); loadQueue();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed.', variant: 'destructive' });
    }
  };

  const deliver = async () => {
    try {
      await api.post(`/courier/ops/shipments/${awb}/pod`, pod);
      toast({ title: 'Delivered', description: 'Proof of delivery captured.' });
      setPod({ receivedBy: '', note: '' }); lookup(awb); loadQueue();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'POD failed.', variant: 'destructive' });
    }
  };

  if (authed === false) {
    return (
      <CourierShell>
        <div className="text-center py-20" data-testid="ops-login-prompt">
          <ScanLine className="h-10 w-10 mx-auto text-black/30 mb-3" />
          <p className="font-black text-xl">Courier staff sign-in required</p>
          <p className="text-black/50 text-sm mt-1">Log in with your courier account to access the console.</p>
          <Link to="/login" className="inline-block mt-4 px-6 py-3 rounded-xl font-black text-black" style={{ background: C.yellow }}>Sign In</Link>
        </div>
      </CourierShell>
    );
  }

  return (
    <CourierShell>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-black">Courier Console</h1>
        {name && <span className="text-sm font-bold text-black/60">{name}</span>}
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-2xl p-6 border border-black/5">
          <p className="font-black mb-3 flex items-center gap-2"><ScanLine className="h-5 w-5" /> Scan / Lookup AWB</p>
          <div className="flex gap-2">
            <input value={awb} onChange={(e) => setAwb(e.target.value.toUpperCase())} placeholder="TKS-1234-5678" data-testid="ops-awb-input"
              className="flex-1 px-4 py-3 rounded-xl border border-black/15 font-mono focus:outline-none focus:ring-2 focus:ring-black"
              onKeyDown={(e) => e.key === 'Enter' && lookup()} />
            <button onClick={() => lookup()} className="px-5 rounded-xl font-black text-black" style={{ background: C.yellow }} data-testid="ops-lookup-btn">Lookup</button>
          </div>

          {shipment && (
            <div className="mt-5" data-testid="ops-shipment">
              <div className="flex items-center justify-between">
                <p className="font-mono font-black">{shipment.awb}</p>
                <span className="px-3 py-1 rounded-full text-xs font-black" style={{ background: C.yellow }} data-testid="ops-status">{(shipment.status || '').replace(/_/g, ' ').toUpperCase()}</span>
              </div>
              <p className="text-sm text-black/60 mt-1">{shipment.origin?.zoneName} → {shipment.destination?.zoneName}</p>

              <div className="grid grid-cols-2 gap-2 mt-4">
                {ACTIONS.map((a) => (
                  <button key={a.s} onClick={() => scan(a.s)} data-testid={`ops-scan-${a.s}`}
                    className="p-3 rounded-xl border-2 border-black/10 font-bold text-sm flex items-center gap-2 hover:border-black">
                    <a.icon className="h-4 w-4" /> {a.label}
                  </button>
                ))}
              </div>

              <input className="w-full mt-2 px-3 py-2 rounded-lg border border-black/15 text-sm" placeholder="Exception/re-attempt reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="ops-reason-input" />

              {(shipment.status === 'exception' || shipment.status === 'returned') && (
                <button onClick={reattempt} data-testid="ops-reattempt-btn"
                  className="w-full mt-2 py-2.5 rounded-lg font-black border-2 border-black flex items-center justify-center gap-2">
                  <RotateCcw className="h-4 w-4" /> Re-attempt Delivery
                </button>
              )}

              <div className="mt-5 p-4 rounded-xl border border-black/10">
                <p className="font-black text-sm mb-2 flex items-center gap-1"><Check className="h-4 w-4" /> Proof of Delivery</p>
                <input className="w-full px-3 py-2 rounded-lg border border-black/15 text-sm mb-2" placeholder="Received by" value={pod.receivedBy} onChange={(e) => setPod({ ...pod, receivedBy: e.target.value })} data-testid="ops-pod-receivedby" />
                <input className="w-full px-3 py-2 rounded-lg border border-black/15 text-sm mb-2" placeholder="Note (optional)" value={pod.note} onChange={(e) => setPod({ ...pod, note: e.target.value })} />
                <button onClick={deliver} className="w-full py-2.5 rounded-lg font-black text-black" style={{ background: C.yellow }} data-testid="ops-deliver-btn">Mark Delivered</button>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl p-6 border border-black/5">
          <p className="font-black mb-3">Out for delivery ({queue.length})</p>
          {queue.length === 0 ? <p className="text-sm text-black/40">Nothing on the road right now.</p> : (
            <div className="space-y-2" data-testid="ops-queue">
              {queue.map((s) => (
                <button key={s.id} onClick={() => lookup(s.awb)} className="w-full text-left p-3 rounded-xl border border-black/10 hover:border-black">
                  <p className="font-mono font-bold text-sm">{s.awb}</p>
                  <p className="text-xs text-black/50">{s.receiver?.name} · {s.destination?.zoneName}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </CourierShell>
  );
}
